import React from 'react';
import PropTypes from 'prop-types';
import { Translate } from 'react-localize-redux';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ResultRow from './ResultRow';
import ExpressionRow, { isDefaultSingleExpression } from './ExpressionRow';
import { TRANSLATION_PREFIX, getDefaultExpression } from '../consts';
import css from '../page.css';

const ruleErrorsShape = PropTypes.shape({
    result: PropTypes.objectOf(PropTypes.bool),
    expressions: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.bool)),
});

function RuleEditor({ rule, onChange, onRemove, onResultChange, errors = null, showErrors = false }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: rule.id });

    const sortableStyle = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
    };

    const onExpressionChange = (expressionIndex, newExpression) => {
        const expression = rule.expression.map((currentExpression, index) =>
            index === expressionIndex ? newExpression : currentExpression
        );
        onChange({ ...rule, expression });
    };

    const onExpressionRemove = expressionIndex => {
        let expression = rule.expression.filter((_, index) => index !== expressionIndex);
        if (!expression.length) {
            expression = [getDefaultExpression()];
        }
        onChange({ ...rule, expression });
    };

    const onExpressionAdd = () => {
        onChange({ ...rule, expression: [...rule.expression, getDefaultExpression()] });
    };

    return (
        <section ref={setNodeRef} style={sortableStyle} className={css.ruleContainer}>
            <button type="button" className={css.removeRuleButton} title="Remove Rule" onClick={onRemove}>
                ×
            </button>
            <div
                className={css.dragHandle}
                title="Drag and reorder rules to prioritize. The value of the first rule matched (from top to bottom) will be used."
                {...attributes}
                {...listeners}
            />
            <div className={css.ruleContent}>
                <h4 className={css.ruleSectionTitle}>
                    <Translate id={`${TRANSLATION_PREFIX}.DO_THIS`} />
                </h4>
                <ResultRow
                    result={rule.result}
                    onChange={newResult => onResultChange(newResult, rule.result)}
                    errors={errors?.result}
                    showErrors={showErrors}
                />
                <h4 className={css.ruleSectionTitle}>
                    <Translate id={`${TRANSLATION_PREFIX}.WHEN`} />
                </h4>
                {rule.expression.map((expression, expressionIndex) => (
                    <ExpressionRow
                        // eslint-disable-next-line react/no-array-index-key
                        key={expressionIndex}
                        expression={expression}
                        onChange={newExpression => onExpressionChange(expressionIndex, newExpression)}
                        onRemove={() => onExpressionRemove(expressionIndex)}
                        onAdd={onExpressionAdd}
                        isLast={expressionIndex === rule.expression.length - 1}
                        removeDisabled={isDefaultSingleExpression(expression, rule.expression.length)}
                        errors={errors?.expressions?.[expressionIndex]}
                        showErrors={showErrors}
                    />
                ))}
            </div>
        </section>
    );
}

RuleEditor.propTypes = {
    rule: PropTypes.shape({
        id: PropTypes.string.isRequired,
        expression: PropTypes.arrayOf(PropTypes.object).isRequired,
        result: PropTypes.objectOf(PropTypes.any).isRequired,
    }).isRequired,
    onChange: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
    onResultChange: PropTypes.func.isRequired,
    errors: ruleErrorsShape,
    showErrors: PropTypes.bool,
};

export default RuleEditor;
